import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './PlanFailure.css';

const PlanFailure = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [failureData, setFailureData] = useState(null);
  const [error, setError] = useState('');

  const companyId = searchParams.get('company_id');
  const planId = searchParams.get('plan_id');
  const noOfWorkers = searchParams.get('no_of_workers');
  const errorCode = searchParams.get('error_code');
  const errorMessage = searchParams.get('error_message');

  useEffect(() => {
    if (companyId && planId && noOfWorkers) {
      fetchPlanFailure();
    } else {
      setError('Invalid failure parameters. Missing required data.');
      setLoading(false);
    }
  }, [companyId, planId, noOfWorkers]);

  const fetchPlanFailure = async () => {
    try {
      setLoading(true);
      setError('');

      const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/plan-failures?company_id=${encodeURIComponent(companyId)}&plan_id=${encodeURIComponent(planId)}&no_of_workers=${encodeURIComponent(noOfWorkers)}${errorCode ? `&error_code=${encodeURIComponent(errorCode)}` : ''}${errorMessage ? `&error_message=${encodeURIComponent(errorMessage)}` : ''}`;
      
      console.log('Plan Failure API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      const data = await response.json();

      if (data.status && data.data) {
        setFailureData(data.data);
        console.log('Plan failure data:', data.data);
      } else {
        setError(data.message || 'Failed to process payment failure. Please contact support.');
      }
    } catch (err) {
      console.error('Plan failure error:', err);
      setError('Failed to process payment failure. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryPayment = () => {
    // Navigate back to teams page to retry
    navigate('/teams');
  };

  const handleContactSupport = () => {
    navigate('/');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const getFailureReason = () => {
    if (errorCode) {
      switch (errorCode) {
        case 'card_declined':
          return 'Your card was declined. Please check your card details or try a different payment method.';
        case 'insufficient_funds':
          return 'Insufficient funds in your account. Please check your balance or try a different card.';
        case 'expired_card':
          return 'Your card has expired. Please use a different card or update your card details.';
        case 'incorrect_cvc':
          return 'The security code (CVC) is incorrect. Please check and try again.';
        case 'processing_error':
          return 'There was a processing error. Please try again in a few minutes.';
        case 'authentication_required':
          return 'Additional authentication is required. Please complete the verification process.';
        default:
          return errorMessage || 'Payment failed due to an unknown error. Please try again.';
      }
    }
    return errorMessage || 'Payment failed. Please try again or contact support.';
  };

  if (loading) {
    return (
      <div className="plan-failure-container">
        <div className="failure-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing payment failure...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="plan-failure-container">
      <div className="failure-card">
        <div className="failure-icon">❌</div>
        <h2>Payment Failed</h2>
        <p className="failure-message">{failureData?.message || getFailureReason()}</p>
        
        {failureData && (
          <div className="failure-details">
            <h3>Transaction Details</h3>
            <div className="detail-row">
              <span className="label">Company ID:</span>
              <span className="value">{failureData.company_id}</span>
            </div>
            <div className="detail-row">
              <span className="label">Plan ID:</span>
              <span className="value">{failureData.plan_id}</span>
            </div>
            <div className="detail-row">
              <span className="label">Number of Workers:</span>
              <span className="value">{failureData.no_of_workers}</span>
            </div>
            {failureData.error_code && (
              <div className="detail-row">
                <span className="label">Error Code:</span>
                <span className="value error-code">{failureData.error_code}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className="value status-failed">{failureData.status || 'failed'}</span>
            </div>
            {failureData.timestamp && (
              <div className="detail-row">
                <span className="label">Time:</span>
                <span className="value">{new Date(failureData.timestamp).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        <div className="failure-actions">
          <button 
            className="btn-primary" 
            onClick={handleRetryPayment}
          >
            Try Again
          </button>
          <button 
            className="btn-secondary" 
            onClick={handleContactSupport}
          >
            Contact Support
          </button>
          <button 
            className="btn-outline" 
            onClick={handleGoHome}
          >
            Go Home
          </button>
        </div>

        <div className="failure-footer">
          <h4>Need Help?</h4>
          <p>If you continue to experience issues, please contact our support team. We're here to help!</p>
          <div className="support-info">
            <p><strong>Email:</strong> support@eworxs.app</p>
            <p><strong>Phone:</strong> +1 (555) 123-4567</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanFailure;
